import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import fetcher, { loadingStatus } from 'helpers/fetcher';
import { getCustomer } from 'helpers/customer';
import { cart, checkout } from 'constants/endpoints';
import {
	addToGuestCartStorage,
	reduceFromGuestCartStorage,
	removeFromGuestCartStorage,
	buildGuestCartPayload,
	getGuestCartFromStorage,
	clearGuestCartStorage,
} from 'helpers/guestCart';

const addToCart = async (product_id, quantity = 1, itemForGuest = null) => {
	if (!getCustomer().token) {
		if (itemForGuest) {
			addToGuestCartStorage(itemForGuest, quantity);
		}
		return {};
	}
	const formData = new FormData();
	formData.append('product_id', product_id);
	formData.append('quantity', quantity);
	const response = await fetcher(cart.ADD, {
		method: 'POST',
		body: formData,
	});
	return response.json();
};

const batchAddToCart = async products => {
	const response = await fetcher(cart.BATCH_ADD, {
		method: 'POST',
		body: JSON.stringify({ products }),
	});
	return response.json();
};

const reduceFromCart = async (product_id, quantity = 1) => {
	if (!getCustomer().token) {
		reduceFromGuestCartStorage(product_id, quantity);
		return {};
	}
	const formData = new FormData();
	formData.append('product_id', product_id);
	formData.append('quantity', quantity);
	const response = await fetcher(cart.REDUCE, {
		method: 'POST',
		body: formData,
	});
	return response.json();
};

const removeFromCart = async key => {
	if (!getCustomer().token) {
		removeFromGuestCartStorage(key);
		return {};
	}
	const formData = new FormData();
	formData.append('key', key);
	const response = await fetcher(cart.REMOVE, {
		method: 'POST',
		body: formData,
	});
	return response.json();
};

const checkoutSave = async data => {
	const formData = new FormData();
	Object.keys(data).forEach(k => formData.append(k, data[k]));
	const response = await fetcher(checkout.SAVE, {
		method: 'POST',
		body: formData,
	});
	return response.json();
};

const checkoutPayment = async action => {
	const response = await fetcher(action);
	return response.json();
};

const fetchCartProducts = createAsyncThunk(
	'cart.PRODUCTS',
	async (arg = {}) => {
		if (!getCustomer().token) {
			return buildGuestCartPayload(getGuestCartFromStorage());
		}
		const response = await fetcher(cart.CART);
		const payload = response ? await response.json() : {};
		return payload;
	}
);

const fetchShippingMethods = createAsyncThunk(
	'cart.SHIPPING_METHODS',
	async () => {
		const response = await fetcher('/index.php?route=checkout/shipping_method');
		return response.json();
	},
);

const fetchPaymentMethods = createAsyncThunk(
	'cart.PAYMENT_METHOD',
	async () => {
		const response = await fetcher('/index.php?route=checkout/payment_method');
		return response.json();
	},
);

const fetchSuccessPage = createAsyncThunk(
	'cart.SUCCESS_PAGE',
	async (orderId = '') => {
		const response = await fetcher(
			`/index.php?route=checkout/success${
				orderId ? `&order_id=${orderId}` : ''
			}`,
		);
		return response.json();
	},
);

/**
 * Бэкенд (checkout::yandex_dostavka) отдаёт json_encode(сырая строка CURL). После 1-го JSON.parse
 * часто остаётся строка JSON от Яндекса — нужен второй parse. Если бэкенд когда-нибудь отдаёт
 * уже объект, второй вызов JSON.parse(...) ломался (TypeError) и в колбэк уходило 0.
 */
const parseCargoCheckPriceEnvelope = rawText => {
	if (rawText == null || String(rawText).trim() === '') {
		return null;
	}
	let first;
	try {
		first = JSON.parse(String(rawText).trim());
	} catch {
		return null;
	}
	if (typeof first === 'object' && first !== null && !Array.isArray(first)) {
		return first;
	}
	if (typeof first === 'string' && first.trim() !== '') {
		try {
			const second = JSON.parse(first);
			return typeof second === 'object' && second !== null && !Array.isArray(second)
				? second
				: null;
		} catch {
			return null;
		}
	}
	return null;
};

/** Успех только при положительной цене; иначе cb(null), чтобы модалка не показывала «0 ₽». */
const calcBasedOnYaDelivery = async (
	storeCoords,
	coords,
	cartWeight = 1,
	cb,
) => {
	const fail = detail => {
		if (detail) {
			console.error('YaDelivery price calc:', detail);
		}
		cb(null);
	};

	try {
		const storeLonLat = [
			parseFloat(storeCoords[1]),
			parseFloat(storeCoords[0]),
		];
		const coordsLonLat = [parseFloat(coords[1]), parseFloat(coords[0])];

		const payload = {
			action:
				'https://b2b.taxi.yandex.net/b2b/cargo/integration/v2/check-price',
			payload: {
				items: [
					{
						quantity: 1,
						weight: cartWeight,
					},
				],
				requirements: {
					taxi_class: 'courier',
				},
				route_points: [
					{ coordinates: storeLonLat },
					{ coordinates: coordsLonLat },
				],
			},
		};

		const response = await fetcher(
			'/index.php?route=checkout/checkout/yandex_dostavka',
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(payload),
			},
		);

		if (!response || typeof response.text !== 'function') {
			return fail('no response');
		}

		const rawText = await response.text();
		if (!response.ok) {
			return fail({
				httpStatus: response.status,
				bodyPreview: rawText.slice(0, 240),
			});
		}

		const cargo = parseCargoCheckPriceEnvelope(rawText);
		if (!cargo) {
			return fail({
				reason: 'unparseable body',
				bodyPreview: rawText.slice(0, 240),
			});
		}

		const pv = cargo.price;
		const n =
			pv !== undefined && pv !== null && pv !== ''
				? Number(String(pv).replace(/\s/g, '').replace(',', '.'))
				: NaN;

		if (Number.isFinite(n) && n > 0) {
			cb(n);
			return;
		}

		return fail({
			message: 'no positive price',
			sample: JSON.stringify(cargo).slice(0, 280),
		});
	} catch (error) {
		return fail(error);
	}
};

const initialState = {
	cartProducts: {
		data: {},
		status: loadingStatus.IDLE,
	},
	shippingMethods: {
		data: {},
		status: loadingStatus.IDLE,
	},
	paymentMethods: {
		data: {},
		status: loadingStatus.IDLE,
	},
	successPage: {
		data: {},
		status: loadingStatus.IDLE,
	},
};

const cartSlice = createSlice({
	name: 'cart',
	initialState,
	reducers: {},
	extraReducers(builder) {
		builder
			.addCase(fetchCartProducts.pending, (state, action) => {
				state.cartProducts.status = loadingStatus.LOADING;
			})
		.addCase(fetchCartProducts.fulfilled, (state, action) => {
			const p = action.payload || {};
			const incomingCount = p.count ?? (p.products?.length ?? 0);
			const existingCount = state.cartProducts.data?.count ?? (state.cartProducts.data?.products?.length ?? 0);
			const refetchAfterMutation = action.meta?.arg?.refetchAfterMutation === true;
			// Не перезаписывать непустую корзину пустым ответом только при фоновом запросе (гонка/другая сессия). После явного удаления/изменения — применять пустой ответ.
			if (incomingCount === 0 && existingCount > 0 && !refetchAfterMutation) {
				state.cartProducts.status = loadingStatus.SUCCEEDED;
				return;
			}
			state.cartProducts.status = loadingStatus.SUCCEEDED;
			state.cartProducts.data = action.payload;
		})
			.addCase(fetchShippingMethods.pending, (state, action) => {
				state.shippingMethods.status = loadingStatus.LOADING;
			})
			.addCase(fetchShippingMethods.fulfilled, (state, action) => {
				state.shippingMethods.status = loadingStatus.SUCCEEDED;
				state.shippingMethods.data = action.payload;
			})
			.addCase(fetchPaymentMethods.pending, (state, action) => {
				state.paymentMethods.status = loadingStatus.LOADING;
			})
			.addCase(fetchPaymentMethods.fulfilled, (state, action) => {
				state.paymentMethods.status = loadingStatus.SUCCEEDED;
				state.paymentMethods.data = action.payload;
			})
			.addCase(fetchSuccessPage.fulfilled, (state, action) => {
				state.successPage.status = loadingStatus.SUCCEEDED;
				state.successPage.data = action.payload;
			});
	},
});

export default cartSlice.reducer;

// export const { postAdded, postUpdated, reactionAdded } = playerSlice.actions;
export {
	reduceFromCart,
	addToCart,
	removeFromCart,
	batchAddToCart,
	checkoutSave,
	checkoutPayment,
	calcBasedOnYaDelivery,
	fetchCartProducts,
	fetchShippingMethods,
	fetchPaymentMethods,
	fetchSuccessPage,
	getGuestCartFromStorage,
	clearGuestCartStorage,
};
